import { Check, Sparkle } from '@phosphor-icons/react';
import { useListMedia } from '@reelplicate/api-client/media';
import type {
  MediaAssetDto,
  Platform as ApiPlatform,
  ScheduledPostDto,
  SocialAccountDto,
  WorkspaceDto,
} from '@reelplicate/api-client/model';
import {
  getListScheduledPostsQueryKey,
  useCreateScheduledPost,
  useGetBestPostTime,
  useUpdateDraftPost,
  useWritePostCaption,
} from '@reelplicate/api-client/scheduled-posts';
import {
  Badge,
  BrandMark,
  Button,
  cn,
  DateField,
  Field,
  Inline,
  Link,
  Modal,
  ModalClose,
  ModalContent,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  TabList,
  Tabs,
  TextArea,
  TextInput,
  TimeField,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';

import type { AccountGroup } from '@/features/calendar/calendar-model';
import type { Owner } from '@/features/media/types';

import { ChooseMediaModal } from './choose-media-modal';
import {
  CAPTION_MAX,
  hasTitle,
  isoDay,
  length,
  type PostForm,
  postZone,
  problems,
  titleMax,
  toBody,
  toInstant,
  toWall,
  zoneLabel,
} from './form';
import { MediaSection } from './media-section';
import { PostPreview } from './post-preview';

const MARKS: Partial<
  Record<ApiPlatform, 'tiktok' | 'instagram' | 'youtube' | 'linkedin' | 'facebook'>
> = {
  TIKTOK: 'tiktok',
  INSTAGRAM: 'instagram',
  YOUTUBE: 'youtube',
  LINKEDIN: 'linkedin',
  FACEBOOK: 'facebook',
};
const PLATFORM_LABELS: Record<ApiPlatform, string> = {
  TIKTOK: 'TikTok',
  INSTAGRAM: 'Instagram',
  YOUTUBE: 'YouTube',
  LINKEDIN: 'LinkedIn',
  FACEBOOK: 'Facebook',
};

export type CreatePostModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: WorkspaceDto;
  groups: AccountGroup[];
  accounts: SocialAccountDto[];
  /** A time picked on the grid. */
  initialAt?: Date;
  /** A draft to finish. */
  draft?: ScheduledPostDto;
};

/**
 * Figma "Modal · Create post" (357:2691 empty, 357:3408 filled): pick the
 * accounts, add a reel or carousel, title and caption (one, or one per
 * platform, or written from the brand profile), and when - now or at a
 * time on the persona's clock (else the workspace's), with the best time
 * suggested. Saves as a
 * draft to finish later. Nothing publishes to platforms yet.
 */
export function CreatePostModal({ open, onOpenChange, ...props }: CreatePostModalProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      {open && <CreatePost {...props} onDone={() => onOpenChange(false)} />}
    </Modal>
  );
}

function CreatePost({
  workspace,
  groups,
  accounts,
  initialAt,
  draft,
  onDone,
}: Omit<CreatePostModalProps, 'open' | 'onOpenChange'> & { onDone: () => void }) {
  const workspaceId = workspace.id;
  const zoneOf = (accountIds: string[]) =>
    postZone(accountIds, accounts, groups, workspace.timezone);
  const queryClient = useQueryClient();
  const create = useCreateScheduledPost();
  const update = useUpdateDraftPost();
  const write = useWritePostCaption();
  const { data: library } = useListMedia(workspaceId, {
    query: { enabled: Boolean(draft?.mediaAssetIds.length) },
  });
  const now = useMemo(() => new Date(), []);

  const [form, setForm] = useState<PostForm>(() =>
    initialForm(
      draft,
      initialAt,
      zoneOf(draft?.accounts.map((a) => a.id) ?? []).timeZone,
    ),
  );
  // The day and time are on the persona's clock when the post goes out as one.
  const zone = zoneOf(form.accountIds);
  const timeZone = zone.timeZone;
  const [timeTouched, setTimeTouched] = useState(Boolean(initialAt || draft));
  const [showErrors, setShowErrors] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState<ApiPlatform>();
  const set = <K extends keyof PostForm>(key: K, value: PostForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // A draft's media, looked up in the library once it loads.
  const [seeded, setSeeded] = useState(!draft?.mediaAssetIds.length);
  if (!seeded && draft && library) {
    setSeeded(true);
    const byId = new Map(library.items.map((a) => [a.id, a]));
    setForm((f) => ({
      ...f,
      media: draft.mediaAssetIds.flatMap((id) => {
        const asset = byId.get(id);
        return asset ? [{ ...asset, via: 'media' as const }] : [];
      }),
    }));
  }

  const picked = accounts.filter((a) => form.accountIds.includes(a.id));
  const platforms = [...new Set(picked.map((a) => a.platform))];

  const best = useGetBestPostTime(
    workspaceId,
    {
      date: form.date ? isoDay(form.date) : isoDay(now),
      ...(form.accountIds.length ? { accountIds: form.accountIds.join(',') } : {}),
    },
    { query: { enabled: Boolean(form.date), throwOnError: false } },
  ).data;
  // Until the time is set by hand, it follows the best time.
  if (!timeTouched && best?.time && best.time !== form.time) {
    setForm((f) => ({ ...f, time: best.time! }));
  }

  const errors = showErrors ? problems(form, timeZone, new Date(), platforms) : {};

  const captionFor = (platform: ApiPlatform) =>
    form.perPlatform ? (form.captions[platform] ?? form.caption) : form.caption;

  const save = async (asDraft: boolean) => {
    if (!asDraft) {
      const found = problems(form, timeZone, new Date(), platforms);
      if (Object.keys(found).length) {
        setShowErrors(true);
        toast.error(Object.values(found)[0]);
        return;
      }
    } else if (form.accountIds.length === 0) {
      setShowErrors(true);
      toast.error('Choose at least one account for the draft.');
      return;
    }
    const data = toBody(form, { draft: asDraft, timeZone, now: new Date() });
    try {
      if (draft) await update.mutateAsync({ id: workspaceId, postId: draft.id, data });
      else await create.mutateAsync({ id: workspaceId, data });
    } catch {
      return; // Shown as a toast.
    }
    await queryClient.invalidateQueries({
      queryKey: getListScheduledPostsQueryKey(workspaceId),
    });
    const n = form.accountIds.length;
    toast.success(
      asDraft
        ? 'Draft saved'
        : form.when === 'now'
          ? `Posted to ${n === 1 ? '1 account' : `${n} accounts`}`
          : `Scheduled for ${n === 1 ? '1 account' : `${n} accounts`}`,
    );
    onDone();
  };

  const writeCaption = async () => {
    try {
      const written = await write.mutateAsync({
        id: workspaceId,
        data: {
          title: form.title.trim() || undefined,
          mediaAssetId: form.media[0]?.id,
          platforms: platforms.length ? platforms : ['TIKTOK'],
        },
      });
      const text = [written.caption, written.hashtags.map((t) => `#${t}`).join(' ')]
        .filter(Boolean)
        .join('\n\n');
      if (form.perPlatform && previewPlatform) {
        setForm((f) => ({
          ...f,
          captions: { ...f.captions, [previewPlatform]: text },
          ...(hasTitle(previewPlatform) && {
            postTitles: { ...f.postTitles, [previewPlatform]: written.title },
          }),
        }));
      } else setForm((f) => ({ ...f, caption: text, postTitle: written.title }));
    } catch {
      // Shown as a toast.
    }
  };

  const n = form.accountIds.length;
  const primary =
    form.when === 'now'
      ? 'Post now'
      : n > 1
        ? `Schedule for ${n} accounts`
        : 'Schedule post';
  const saving = create.isPending || update.isPending;
  const owners: Owner[] = groups.map((g) => ({
    id: g.id === 'brand' ? 'brand' : g.id,
    name: g.name,
  }));

  return (
    <ModalContent
      title={draft ? 'Finish draft' : 'Create post'}
      description="Pick where it goes, add the reel and when it should go out."
      className={cn(
        'h-[min(1016px,calc(100vh-64px))] w-260',
        '[&_[data-slot=modal-header]]:border-border-subtle [&_[data-slot=modal-header]]:border-b',
        '[&_[data-slot=modal-body]]:flex [&_[data-slot=modal-body]]:p-0',
        '[&_[data-slot=modal-footer]]:bg-bg-surface',
      )}
      footerStart={
        <Button variant="ghost" disabled={saving} onClick={() => void save(true)}>
          Save as draft
        </Button>
      }
      footer={
        <>
          <ModalClose asChild>
            <Button variant="outline">Cancel</Button>
          </ModalClose>
          <Button loading={saving} disabled={n === 0} onClick={() => void save(false)}>
            {primary}
          </Button>
        </>
      }
    >
      <Stack gap={5} className="w-160 shrink-0 overflow-y-auto px-6 pt-6 pb-4">
        <AccountPicker
          groups={groups}
          accounts={accounts}
          selected={form.accountIds}
          onChange={(ids) =>
            setForm((f) => {
              const next = zoneOf(ids).timeZone;
              // Keep the picked moment; show it on the new zone's clock.
              if (next === timeZone || !f.date || !f.time) {
                return { ...f, accountIds: ids };
              }
              const wall = toWall(toInstant(f.date, f.time, timeZone), next);
              return { ...f, accountIds: ids, date: wall.date, time: wall.time };
            })
          }
          error={errors.accounts}
        />

        <MediaSection
          workspaceId={workspaceId}
          type={form.dataType}
          onTypeChange={(type) => setForm((f) => ({ ...f, dataType: type, media: [] }))}
          media={form.media}
          onMediaChange={(next) => setForm((f) => ({ ...f, media: next(f.media) }))}
          onChoose={() => setChoosing(true)}
          error={errors.media}
        />

        <Field
          label="Name"
          helper="For you, in Reelplicate; not posted"
          error={errors.title}
        >
          <TextInput
            value={form.title}
            placeholder="e.g. Foam art in 10 seconds"
            maxLength={200}
            onChange={(event) => set('title', event.target.value)}
          />
        </Field>

        <CaptionSection
          form={form}
          platforms={platforms}
          tab={
            previewPlatform && platforms.includes(previewPlatform)
              ? previewPlatform
              : platforms[0]
          }
          onTabChange={setPreviewPlatform}
          onChange={setForm}
          onWrite={() => void writeCaption()}
          writing={write.isPending}
          error={errors.caption}
          titleError={errors.postTitle}
        />

        <Stack as="section" aria-labelledby="post-when" gap={2}>
          <h3 id="post-when" className="text-label-lg text-text-primary">
            When
          </h3>
          <RadioGroup
            orientation="horizontal"
            aria-labelledby="post-when"
            value={form.when}
            onValueChange={(value) => set('when', value as PostForm['when'])}
            className="gap-5"
          >
            <Radio value="now" label="Post now" />
            <Radio value="schedule" label="Schedule" />
          </RadioGroup>
          {form.when === 'schedule' && (
            <Inline gap={3}>
              <DateField
                aria-label="Day"
                value={form.date ?? null}
                onValueChange={(date) => set('date', date)}
                disabledDays={{ before: toWall(now, timeZone).date }}
                today={toWall(now, timeZone).date}
                className="w-55"
              />
              <TimeField
                aria-label="Time"
                value={form.time ?? null}
                step={15}
                onValueChange={(time) => {
                  setTimeTouched(true);
                  set('time', time);
                }}
                className="w-30"
              />
              {best?.time && (
                <Badge tone="brand" size="sm">
                  Best time: {best.time}
                </Badge>
              )}
            </Inline>
          )}
          {errors.when && (
            <p role="alert" className="text-caption text-status-danger-fg">
              {errors.when}
            </p>
          )}
          <p className="text-caption text-text-tertiary">
            {zoneLabel(timeZone, now)} ·{' '}
            {zone.persona
              ? `${zone.persona}’s time zone`
              : 'change in Settings → General'}
          </p>
        </Stack>
      </Stack>

      <PostPreview
        accounts={picked}
        platform={previewPlatform}
        onPlatformChange={setPreviewPlatform}
        type={form.dataType}
        media={form.media}
        captionFor={captionFor}
      />

      <ChooseMediaModal
        open={choosing}
        onOpenChange={setChoosing}
        workspaceId={workspaceId}
        type={form.dataType}
        owners={owners}
        onAdd={(assets: MediaAssetDto[]) => {
          const added = assets.map((a) => ({ ...a, via: 'media' as const }));
          setForm((f) => ({
            ...f,
            media:
              f.dataType === 'reel'
                ? added.slice(0, 1)
                : [
                    ...f.media,
                    ...added.filter((a) => !f.media.some((m) => m.id === a.id)),
                  ],
            title: f.title || (f.dataType === 'reel' ? (added[0]?.title ?? '') : ''),
          }));
        }}
      />
    </ModalContent>
  );
}

function initialForm(
  draft: ScheduledPostDto | undefined,
  initialAt: Date | undefined,
  timeZone: string,
): PostForm {
  const at = draft ? new Date(draft.scheduledAt) : (initialAt ?? new Date());
  const wall = toWall(at, timeZone);
  if (!draft) {
    return {
      accountIds: [],
      dataType: 'reel',
      media: [],
      title: '',
      caption: '',
      postTitle: '',
      perPlatform: false,
      captions: {},
      postTitles: {},
      when: 'schedule',
      date: wall.date,
      time: initialAt ? wall.time : undefined,
    };
  }
  const captions: PostForm['captions'] = {};
  for (const account of draft.accounts) {
    if (account.caption) captions[account.platform] ??= account.caption;
  }
  const postTitles: PostForm['postTitles'] = {};
  for (const account of draft.accounts) {
    if (account.postTitle && hasTitle(account.platform)) {
      postTitles[account.platform] ??= account.postTitle;
    }
  }
  const distinct = new Set(draft.accounts.map((a) => a.caption ?? ''));
  const distinctTitles = new Set(Object.values(postTitles));
  return {
    accountIds: draft.accounts.map((a) => a.id),
    dataType: draft.dataType === 'carousel' ? 'carousel' : 'reel',
    media: [],
    title: draft.title === 'Untitled post' ? '' : draft.title,
    caption: draft.accounts[0]?.caption ?? '',
    postTitle: Object.values(postTitles)[0] ?? '',
    perPlatform: distinct.size > 1 || distinctTitles.size > 1,
    captions,
    postTitles,
    when: 'schedule',
    date: wall.date,
    time: wall.time,
  };
}

/** Figma "Post to": the accounts by owner as toggle chips. */
function AccountPicker({
  groups,
  accounts,
  selected,
  onChange,
  error,
}: {
  groups: AccountGroup[];
  accounts: SocialAccountDto[];
  selected: string[];
  onChange: (ids: string[]) => void;
  error?: string;
}) {
  const owned = (group: AccountGroup) =>
    accounts.filter((a) => (a.personaId ?? 'brand') === group.id);
  const n = selected.length;
  return (
    <Stack as="section" aria-labelledby="post-to" gap={2}>
      <Inline gap={2}>
        <h3 id="post-to" className="text-label-lg text-text-primary flex-1">
          Post to
        </h3>
        {n > 0 && (
          <span className="text-caption text-text-secondary">
            {n === 1 ? '1 account' : `${n} accounts · crosspost`}
          </span>
        )}
      </Inline>
      {groups
        .filter((g) => owned(g).length > 0)
        .map((group) => (
          <Inline key={group.id} gap={3}>
            <span className="text-body-sm text-text-secondary w-24 shrink-0">
              {group.name}
            </span>
            <Inline
              as="ul"
              gap={2}
              wrap
              aria-label={group.name}
              className="min-w-0 flex-1"
            >
              {owned(group).map((account) => {
                const on = selected.includes(account.id);
                const mark = MARKS[account.platform];
                return (
                  <li key={account.id}>
                    <button
                      type="button"
                      aria-pressed={on}
                      aria-label={`${account.handle} · ${PLATFORM_LABELS[account.platform]}`}
                      onClick={() =>
                        onChange(
                          on
                            ? selected.filter((id) => id !== account.id)
                            : [...selected, account.id],
                        )
                      }
                      className={cn(
                        'text-label-sm focus-visible:shadow-focus-ring flex h-8 items-center gap-2 rounded-full border pr-3 outline-none transition-colors duration-[120ms]',
                        on
                          ? 'bg-brand-subtle border-brand-strong text-text-brand pl-2'
                          : 'bg-bg-surface border-border-default text-text-primary hover:bg-bg-subtle pl-2.5',
                      )}
                    >
                      {on && <Check aria-hidden className="size-3.5" />}
                      {mark && <BrandMark name={mark} size={16} />}
                      {account.handle}
                    </button>
                  </li>
                );
              })}
            </Inline>
          </Inline>
        ))}
      <p
        className={cn(
          'text-caption',
          error ? 'text-status-danger-fg' : 'text-text-tertiary',
        )}
        role={error ? 'alert' : undefined}
      >
        {error ??
          (n > 1
            ? 'More than one account = a crosspost: one post per account, stacked icons on the calendar.'
            : 'Choose one or more accounts. Each gets its own post.')}
      </p>
    </Stack>
  );
}

/** Figma "Caption": one caption, or one per platform; Write with AI; the count. */
function CaptionSection({
  form,
  platforms,
  tab,
  onTabChange,
  onChange,
  onWrite,
  writing,
  error,
  titleError,
}: {
  form: PostForm;
  platforms: ApiPlatform[];
  tab: ApiPlatform | undefined;
  onTabChange: (platform: ApiPlatform) => void;
  onChange: (update: (form: PostForm) => PostForm) => void;
  onWrite: () => void;
  writing: boolean;
  error?: string;
  titleError?: string;
}) {
  const labelId = useId();
  const perPlatform = form.perPlatform && platforms.length > 0;
  const text = perPlatform && tab ? (form.captions[tab] ?? form.caption) : form.caption;
  const setText = (value: string) =>
    onChange((f) =>
      perPlatform && tab
        ? { ...f, captions: { ...f.captions, [tab]: value } }
        : { ...f, caption: value },
    );
  const count = length(text);
  // The title field: the tab's own platform, or every titled one picked.
  const titled =
    perPlatform && tab ? (hasTitle(tab) ? [tab] : []) : platforms.filter(hasTitle);
  const maxTitle = titled.length ? titleMax(titled) : 0;
  const title =
    perPlatform && tab && hasTitle(tab)
      ? (form.postTitles[tab] ?? form.postTitle)
      : form.postTitle;
  const setTitle = (value: string) =>
    onChange((f) =>
      perPlatform && tab && hasTitle(tab)
        ? { ...f, postTitles: { ...f.postTitles, [tab]: value } }
        : { ...f, postTitle: value },
    );

  return (
    <Stack as="section" aria-labelledby={labelId} gap={2}>
      <Inline gap={2}>
        <h3 id={labelId} className="text-label-lg text-text-primary flex-1">
          Caption
        </h3>
        {platforms.length > 1 && (
          <Link asChild size="sm" tone="brand">
            <button
              type="button"
              onClick={() =>
                onChange((f) =>
                  f.perPlatform
                    ? { ...f, perPlatform: false }
                    : {
                        ...f,
                        perPlatform: true,
                        captions: Object.fromEntries(
                          platforms.map((p) => [p, f.captions[p] ?? f.caption]),
                        ),
                        postTitles: Object.fromEntries(
                          platforms
                            .filter(hasTitle)
                            .map((p) => [p, f.postTitles[p] ?? f.postTitle]),
                        ),
                      },
                )
              }
            >
              {form.perPlatform ? 'Use one caption' : 'Customise per platform'}
            </button>
          </Link>
        )}
      </Inline>
      {perPlatform && tab && (
        <Tabs value={tab} onValueChange={(value) => onTabChange(value as ApiPlatform)}>
          <TabList variant="pill" aria-label="Caption for">
            {platforms.map((p) => (
              <Tab key={p} value={p}>
                {PLATFORM_LABELS[p]}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      )}
      {titled.length > 0 && (
        <Field
          label={perPlatform && tab ? `Title for ${PLATFORM_LABELS[tab]}` : 'Title'}
          helper={
            perPlatform
              ? 'Goes in its own title field, above the caption'
              : `Goes in ${titled.map((p) => PLATFORM_LABELS[p]).join(' and ')}’s own title field, above the caption`
          }
          count={length(title)}
          maxLength={maxTitle}
          error={titleError}
        >
          <TextInput
            value={title}
            aria-invalid={Boolean(titleError) || length(title) > maxTitle}
            placeholder="A short hook"
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
      )}
      <TextArea
        {...(perPlatform && tab
          ? { 'aria-label': `Caption for ${PLATFORM_LABELS[tab]}` }
          : { 'aria-labelledby': labelId })}
        aria-invalid={Boolean(error) || count > CAPTION_MAX}
        value={text}
        placeholder="Write a caption, or ask Reelplicate to write one from your brand profile."
        onChange={(event) => setText(event.target.value)}
        className="h-28"
      />
      <Inline gap={2}>
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={Sparkle}
          loading={writing}
          onClick={onWrite}
        >
          Write with AI
        </Button>
        <span className="flex-1" />
        <span
          className={cn(
            'text-caption',
            count > CAPTION_MAX ? 'text-status-danger-fg' : 'text-text-tertiary',
          )}
        >
          {count.toLocaleString('en-US')} / {CAPTION_MAX.toLocaleString('en-US')}
        </span>
      </Inline>
      {error && (
        <p role="alert" className="text-caption text-status-danger-fg">
          {error}
        </p>
      )}
    </Stack>
  );
}
